/**
 * UsersSearchList
 *
 * Isolates user search and list so only this component re-renders on keystroke or list changes.
 * Notifies parent: onUsersChange (ids), onHasUsersChange (has users), onReady (refetch).
 * Single responsibility: search + infinite list; parent owns selection/edit/delete (Dependency Inversion).
 */

import React, { useEffect, useMemo, useRef, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { Plus, Loader2 } from 'lucide-react';
import ListSearchInput from '../common/ListSearchInput';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useUsersInfinite } from '../../hooks/useUsersInfinite';
import ListCheckbox from '../common/ListCheckbox';
import { UserRow } from './UserRow';
import { UsersTableSkeleton } from '../skeletons/UsersTableSkeleton';

const SEARCH_DEBOUNCE_MS = 350;
const USERS_PAGE_LIMIT = 10;

const TH_HEADER =
  'px-6 py-3 text-left font-["Roboto:Medium",sans-serif] font-medium text-[14px] text-black';

const UsersSearchHeader = memo(function UsersSearchHeader({
  searchValue,
  onSearchChange,
  onInviteClick,
}) {
  return (
    <div className="flex items-center justify-between mb-6 gap-4">
      <div className="flex-1 max-w-md min-w-0">
        <ListSearchInput
          value={searchValue}
          onChange={onSearchChange}
          placeholder="Search users"
          ariaLabel="Search users"
          className="w-full min-w-0"
        />
      </div>
      <button
        type="button"
        onClick={onInviteClick}
        className="flex items-center gap-2 px-6 py-2.5 border-2 border-[#61b8e7] text-[#61b8e7] rounded-lg font-['Roboto:Bold',sans-serif] font-bold text-[14px] hover:bg-[#61b8e7]/5 transition-colors whitespace-nowrap"
      >
        <Plus className="size-5" />
        Invite New User
      </button>
    </div>
  );
});

UsersSearchHeader.propTypes = {
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onInviteClick: PropTypes.func.isRequired,
};

const SearchingState = memo(function SearchingState() {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-xl bg-[#f8fbfd] border border-[#e5eaf0] min-h-[200px] px-6 py-10"
      role="status"
      aria-live="polite"
      aria-label="Searching users"
    >
      <Loader2 className="w-8 h-8 text-[#61b8e7] animate-spin mb-4" aria-hidden />
      <p className="font-['Roboto:Regular',sans-serif] text-[15px] text-[#6e6e71] text-center">
        Searching users...
      </p>
      <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-[#9e9e9e] text-center mt-1">
        Waiting for you to finish typing...
      </p>
    </div>
  );
});

const EmptyListMessage = memo(function EmptyListMessage({ message }) {
  return (
    <div className="py-12 text-center">
      <p className="font-['Roboto:Regular',sans-serif] text-gray-500">{message}</p>
    </div>
  );
});

EmptyListMessage.propTypes = {
  message: PropTypes.string.isRequired,
};

const LoadMoreSection = memo(function LoadMoreSection({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}) {
  if (!hasNextPage) return null;
  return (
    <div className="pt-2 pb-2 flex flex-col items-center justify-center gap-3 border-t border-gray-100">
      {isFetchingNextPage ? (
        <div className="flex items-center gap-3 text-[#61b8e7] py-4">
          <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
          <span className="font-['Roboto:Regular',sans-serif] text-sm">
            Loading more users...
          </span>
        </div>
      ) : (
        <button
          type="button"
          onClick={onLoadMore}
          className="w-full max-w-[400px] px-6 py-3 border-2 border-[#61b8e7] rounded-lg text-[#61b8e7] hover:bg-[#61b8e7] hover:text-white transition-all font-['Roboto:Medium',sans-serif] text-sm shadow-sm hover:shadow-md"
        >
          Load More
        </button>
      )}
    </div>
  );
});

LoadMoreSection.propTypes = {
  hasNextPage: PropTypes.bool.isRequired,
  isFetchingNextPage: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
};

export const UsersSearchList = ({
  organizationId,
  selectedUserIds,
  onSelectUser,
  onSelectAll,
  onEditUser,
  onDeleteUser,
  onReady,
  onHasUsersChange,
  onUsersChange,
  onInviteClick,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  const {
    users,
    totalCount,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useUsersInfinite({
    organizationId: organizationId || '',
    limit: USERS_PAGE_LIMIT,
    search: debouncedSearch ?? '',
    enabled: !!organizationId,
  });

  const searchTrimmed = (searchQuery && searchQuery.trim()) || '';
  const debouncedTrimmed = (debouncedSearch && String(debouncedSearch).trim()) || '';
  const isDebouncing = searchTrimmed !== debouncedTrimmed;
  const isSearchActive = searchTrimmed.length > 0;
  const isSearching = isSearchActive && (isDebouncing || (isFetching && users.length === 0));

  const displayedUsers = useMemo(() => {
    if (isSearching) return [];
    return users;
  }, [users, isSearching]);

  // Notify parent of refetch API once so parent can refresh list after create/update/delete
  useEffect(() => {
    onReady?.({ refetch });
  }, [onReady, refetch]);

  // Notify parent only when "has at least one user" (with empty search) actually changes
  const hasUsers = !debouncedTrimmed && totalCount > 0;
  const prevHasUsersRef = useRef(hasUsers);
  useEffect(() => {
    if (prevHasUsersRef.current !== hasUsers) {
      prevHasUsersRef.current = hasUsers;
      onHasUsersChange?.(hasUsers);
    }
  }, [hasUsers, onHasUsersChange]);

  // Notify parent only when the list of user ids actually changes (avoids re-render loops)
  const userIdsKey = useMemo(
    () => displayedUsers.map((u) => u.id).join(','),
    [displayedUsers],
  );
  const prevIdsRef = useRef('');
  useEffect(() => {
    if (userIdsKey !== prevIdsRef.current) {
      prevIdsRef.current = userIdsKey;
      onUsersChange?.(displayedUsers);
    }
  }, [userIdsKey, displayedUsers, onUsersChange]);

  const handleToggleSelectAll = useCallback(() => {
    if (selectedUserIds.size === displayedUsers.length && displayedUsers.length > 0) {
      onSelectAll([]);
    } else {
      onSelectAll(displayedUsers.map((u) => u.id));
    }
  }, [selectedUserIds.size, displayedUsers, onSelectAll]);

  // Skeleton only on first load (no search). When user searches, show "Searching..." loader until results, not skeleton.
  const isInitialLoad = isLoading && users.length === 0 && !isSearchActive;
  const isEmptyNoSearch = !isLoading && users.length === 0 && !isSearching && !isSearchActive;
  const showListArea = users.length > 0 || isSearchActive || isSearching;

  return (
    <>
      {/* Search + Invite: always visible when we show list area; for empty no-search we show CTA below */}
      {showListArea && (
        <UsersSearchHeader
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          onInviteClick={onInviteClick}
        />
      )}

      {isInitialLoad && <UsersTableSkeleton />}

      {!isInitialLoad && isEmptyNoSearch && (
        <div className="flex items-center justify-center mt-20">
          <button
            type="button"
            onClick={onInviteClick}
            className="group bg-gradient-to-br from-[#f8fbfd] to-white border-2 border-[#61b8e7]/30 rounded-[12px] h-[90px] flex items-center justify-center gap-3 hover:border-[#61b8e7] hover:shadow-md hover:shadow-[#61b8e7]/10 transition-all w-full max-w-[500px]"
          >
            <div className="bg-[#61b8e7] rounded-[6px] w-[32px] h-[32px] flex items-center justify-center group-hover:scale-110 transition-transform">
              <Plus className="w-[18px] h-[18px] text-white" strokeWidth={3} />
            </div>
            <span
              className="font-['Roboto:Medium',sans-serif] font-medium text-[15px] text-[#61b8e7] group-hover:text-[#4da3d1]"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              Invite New User
            </span>
          </button>
        </div>
      )}

      {!isInitialLoad && showListArea && (
        <>
          {isSearching ? (
            <SearchingState />
          ) : (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-200">
                    <th className="px-6 py-3 text-left w-[50px]">
                      <ListCheckbox
                        checked={
                          selectedUserIds.size === displayedUsers.length && displayedUsers.length > 0
                        }
                        onToggle={handleToggleSelectAll}
                        ariaLabel="Select all users"
                      />
                    </th>
                    <th className={TH_HEADER}>Name</th>
                    <th className={TH_HEADER}>Email</th>
                    <th className={TH_HEADER}>Role</th>
                    <th className={TH_HEADER}>Scope</th>
                    <th className={`${TH_HEADER} w-[120px]`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedUsers.map((user) => (
                    <UserRow
                      key={user.id}
                      user={user}
                      isSelected={selectedUserIds.has(user.id)}
                      onToggleSelect={onSelectUser}
                      onEdit={onEditUser}
                      onDelete={onDeleteUser}
                    />
                  ))}
                </tbody>
              </table>

              {displayedUsers.length === 0 && (
                <EmptyListMessage
                  message={
                    isSearchActive
                      ? `No users found matching "${debouncedSearch}"`
                      : 'No users yet. Invite your first user above.'
                  }
                />
              )}

              {displayedUsers.length > 0 && (
                <>
                  <LoadMoreSection
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    onLoadMore={fetchNextPage}
                  />
                  <div className="py-2 border-t border-gray-100 text-center">
                    <span className="font-['Roboto:Regular',sans-serif] text-xs text-gray-500">
                      {`Showing ${displayedUsers.length} of ${totalCount} users`}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};

UsersSearchList.propTypes = {
  organizationId: PropTypes.string,
  selectedUserIds: PropTypes.instanceOf(Set).isRequired,
  onSelectUser: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onEditUser: PropTypes.func.isRequired,
  onDeleteUser: PropTypes.func.isRequired,
  onReady: PropTypes.func,
  onHasUsersChange: PropTypes.func,
  onUsersChange: PropTypes.func,
  onInviteClick: PropTypes.func.isRequired,
};
